import {
  Component,
  ChangeDetectionStrategy,
  OnInit,
  Input,
  Output,
  OnChanges,
  SimpleChanges,
  EventEmitter,
} from '@angular/core';

import { IQueryOptions, IMetricFilterWrapper } from 'app/hub-components/interfaces/query-filter.interface';
import { MetricFilterSource } from 'app/hub-components/models/metric-filter-source.model';
import { MetricFilter } from 'app/hub-components/models/metric-filter.model';
import { FilterService } from 'app/hub-components/services/filter/filter.service';
import { Query } from '../../../../models/analytics-api/query.model';
import { Metric, Schema } from '../../../../models/analytics-api/schema.model';
import { QueryFilterOption } from '../../../../models/query-option.model';
import { QueryService } from '../../../../services/query/query.service';
import { SelectItem } from 'primeng/api';

@Component({
  selector: 'metric-filters',
  templateUrl: './metric-filters.component.html',
  styleUrls: ['./metric-filters.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})

export class MetricFiltersComponent implements OnInit, OnChanges {
  @Input() datasetName: string;
  @Input() schema: Schema;
  @Input() compatibleMetrics: Metric[];
  @Input() filters: IMetricFilterWrapper[] = [];
  @Input() interval: string;
  @Input() showDefaultFilter: boolean = false;
  @Input() readonly: boolean = false;
  @Input() filterOptions: QueryFilterOption[] = [];

  @Output() filtersData: EventEmitter<{ filters: IMetricFilterWrapper[], emptyFilterWarnings: boolean[][] }> = new EventEmitter();

  public options: IQueryOptions = {
    showMultipleOptions: true,
    showOrFilters: true,
    showEmptyFilterWarning: true,
  };

  public metricFilterSelection: string[][] = [];
  public filterSourceWarnings: boolean[][] = [];
  public query: Query;
  public filterSourceList: MetricFilterSource[];
  public filterSourceOptions: SelectItem[] = [];
  public loading: boolean[][] = [];
  public emptyFilterWarnings: boolean[][] = [];
  public filtersArray: IMetricFilterWrapper[] = [];
  public noFiltersAddedMsg = [{ severity: 'secondary', detail: 'You have not added any filters yet. Click on the plus (+) button below to start adding filters.' }];

  constructor(
    private queryService: QueryService,
    private filterService: FilterService,
  ) { }

  public ngOnInit(): void {
    this.configDefaults();
    this.initDefaults();
  }

  public ngOnChanges(changes: SimpleChanges) {
    if (
      (
        changes['datasetName'] && !changes['datasetName'].isFirstChange() &&
        changes['schema'] && !changes['schema'].isFirstChange()
      ) ||
      (!!changes.readonly && !changes.readonly.currentValue)
    ) {
      this.resetDefaults();
      this.configDefaults();
      this.initDefaults();
    }
  }

  public changeFilterSource(metricName: string, indexAnd: number, indexOr: number, resetValues: boolean = false): void {
    const filterSource = this.filterSourceList.find(fs => fs.name === metricName);
    this.filterService.changeFilterSource(this.filtersArray, filterSource, indexAnd, indexOr, resetValues);
    this.checkEmptyFilters(null, indexAnd, indexOr);
  }

  public changeFilterType(filterType: QueryFilterOption, blockIdx: number, filterIdx: number, resetValues?: boolean) {
    this.filterService.changeFilterType(this.filtersArray, this.metricFilterSelection, filterType, blockIdx, filterIdx, resetValues);
    this.checkEmptyFilters(null, blockIdx, filterIdx);
  }

  public addFilter(indexAnd: number, indexOr: number): void {
    this.filterService.addFilter(this.filtersArray, this.metricFilterSelection, this.defaultFilter, this.filterOptions, indexAnd, indexOr);
    this.changeFilterSource(this.firstCompatibleMetricName(), indexAnd, indexOr + 1);
    this.checkEmptyFilters(null, indexAnd, indexOr + 1);
  }

  public addBlock(): void {
    const newIndex = this.filtersArray.length;
    this.initArrays(newIndex);
    this.filterService.addBlock(this.filtersArray, this.metricFilterSelection, this.defaultFilter, this.filterOptions);
    this.changeFilterSource(this.firstCompatibleMetricName(), newIndex, 0);
    this.checkEmptyFilters(null, newIndex, 0);
  }

  public removeFilter(indexAnd: number, indexOr: number): void {
    this.filterService.removeFilter(this.filtersArray, this.metricFilterSelection, this.emptyFilterWarnings, indexAnd, indexOr);
    this.updateMetricFilters();
  }

  public checkEmptyFilters(value, indexAnd, indexOr) {
    if (this.options.showEmptyFilterWarning) {
      const filterType = this.metricFilterSelection[indexAnd][indexOr];
      this.emptyFilterWarnings[indexAnd][indexOr] = value === null && (filterType !== 'not_na' && filterType !== 'na');
    }
    this.updateMetricFilters();
  }

  public findFilterSource(filter: MetricFilter) {
    return this.filterService.findFilterSource(filter, this.filterSourceList);
  }

  public findMetricFilterOption(filter: string) {
    return this.filterService.findMetricFilterOption(this.filterOptions, filter);
  }

  public trackByFiltersArray(index: number) {
    return 'block-' + index;
  }

  public trackByFiltersExpressions(indexAnd: number, indexOr: number, item: any) {
    return item.key + '-' + item.type + '-' + indexAnd + '-' + indexOr;
  }

  public isPercentFormat(filter: MetricFilter): boolean {
    const filterSource = this.findFilterSource(filter);
    if (filterSource?.format instanceof Object) {
      return filterSource?.format['format'] === 'percent';
    } else {
      return filterSource?.format === 'percent';
    }
  }

  public setFilterValue(value: number, filter: MetricFilter, i: number, j: number): void {
    if (this.isPercentFormat(filter)) {
      filter.value = value !== null ? value / 100 : null;
    } else {
      filter.value = value;
    }
    this.checkEmptyFilters(filter.value, i, j);
  }

  public formatValue(filter: MetricFilter): number | null {
    if (this.isPercentFormat(filter)) {
      return filter.value !== null ? filter.value * 100 : null;
    } else {
      return filter.value;
    }
  }

  public findFilterOption(filter: MetricFilter) {
    this.filterSourceOptions.find(option => option.value === filter.metric.name);
  }

  private isCompatible(metricFilterSource: MetricFilterSource) {
    return (!this.compatibleMetrics || this.compatibleMetrics.findIndex(metric => metric.displayName === metricFilterSource.name || metric.name === metricFilterSource.name) !== -1);
  }

  private firstCompatibleMetricName(): string {
    return this.filterSourceOptions.find(option => !option.disabled).value;
  }

  private configDefaults() {
    this.filtersArray = this.processFilters(this.filters);
    this.filterSourceList = this.queryService.getMetricFilterSourceList(this.schema.metrics);

    this.configDefaultQuery();
    this.configFilterSourceOptions();

    this.filters.forEach((blocks, indexAnd) => {
      this.initArrays(indexAnd);
      blocks.expressions.forEach((filter, indexOr) => {
        const filterSource = this.findFilterSource(filter);
        this.changeFilterSource(filterSource.name, indexAnd, indexOr);
        this.metricFilterSelection[indexAnd][indexOr] = filter.type;
        this.checkEmptyFilters(filter.value, indexAnd, indexOr);
        this.checkFilterWarningStatus(indexAnd, indexOr, filterSource);
      });
    });
  }

  private checkFilterWarningStatus(indexAnd: number, indexOr: number, filter: MetricFilterSource) {
    this.filterSourceWarnings[indexAnd][indexOr] = !this.compatibleMetrics.find(metric => metric.name === filter.metric.name);
  }

  private initDefaults() {
    if (this.showDefaultFilter && !this.filtersArray.length) {
      this.addBlock();
    }
  }

  private processFilters(data) {
    const formatted = JSON.parse(JSON.stringify(data));
    return this.queryService.processMetricFilters(formatted);
  }

  private configDefaultQuery() {
    const DEFAULT_QUERY_JSON = {
      dataset: this.datasetName,
      interval: this.interval,
      metrics: [],
    };
    this.query = new Query(DEFAULT_QUERY_JSON, this.schema);
  }

  private configFilterSourceOptions() {
    this.filterSourceOptions = this.filterSourceList.map(filterSource => {
      return {
        label: filterSource.displayName,
        value: filterSource.name,
        disabled: !this.isCompatible(filterSource),
      };
    });
  }

  private updateMetricFilters() {
    this.queryService.updateMetricFilters(this.filtersArray);
    this.filtersData.emit({ filters: this.filtersArray, emptyFilterWarnings: this.emptyFilterWarnings });
  }

  private get defaultFilter(): MetricFilter {
    return new MetricFilter({
      type: this.filterOptions[0].type,
      key: this.firstCompatibleMetricName(),
      value: null,
      metric: this.filterSourceList.find(fs => fs.metric.name === this.firstCompatibleMetricName()).metric,
    });
  }

  private initArrays(index) {
    this.metricFilterSelection[index] = [];
    this.filterSourceWarnings[index] = [];
    this.emptyFilterWarnings[index] = [];
  }

  private resetDefaults() {
    this.filtersArray = [];
    this.filterSourceList = [];
    this.metricFilterSelection = [];
    this.filterSourceWarnings = [];
    this.emptyFilterWarnings = [];
  }
}
